// Navigator three-column layout (§11.1, §11.6).
//
//   .nav-sidebar (260px) | .nav-main (flex)          | .nav-details (40vw)
//   project tree         | logo / upload / preview   | dashboard or details

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSWRConfig } from "swr";
import { Document, Page } from "react-pdf";
import { useTranslation } from "react-i18next";

import { useExamTasks, useProjects } from "../api/hooks";
import type { Paper, PaperStatus, Project } from "../api/types";
import { useExamUiStore } from "../state/examUiStore";
import { useModelUiStore } from "../state/modelUiStore";
import { ModelAssignPreview } from "../components/ModelAssignPreview";
import { advanceTour } from "../components/GuidedTour";
import { ModelSolutionStack } from "../components/ModelSolutionStack";
import { DetailsPanel, EXAM_PAPER_ID, MODEL_PAPER_ID } from "../components/DetailsPanel";
import { ProjectTree } from "../components/ProjectTree";
import { UploadPapersDialog } from "../components/UploadPapersDialog";
import { useDeviceMode } from "../device/DeviceContext";
import { useTourStore } from "../state/tourStore";
import "../lib/pdfWorker";

import "./Navigator.css";

export function Navigator() {
  const [params] = useSearchParams();
  const archived = params.get("archived") === "1";
  const tourActive = useTourStore((s) => s.active);
  const mode = useDeviceMode();
  const { data, error, isLoading } = useProjects(archived, tourActive);

  // Prevent the browser from zooming the whole page on ctrl+scroll / pinch.
  // PreviewPage handles the actual zoom within the preview area.
  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) e.preventDefault();
    };
    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, []);

  if (error) {
    return (
      <section className="nav-error">
        <h2>Couldn't load projects</h2>
        <p>{error.message}</p>
      </section>
    );
  }

  const projects = data?.projects ?? [];
  const selectedProjectId = params.get("project");
  const selectedPaperId = params.get("paper");

  if (mode === "mobile") {
    return (
      <NavigatorMobile
        projects={projects}
        isLoading={isLoading}
        selectedProjectId={selectedProjectId}
        selectedPaperId={selectedPaperId}
        archived={archived}
      />
    );
  }

  return (
    <div className="nav-shell">
      <aside className="nav-sidebar" data-tour="sidebar">
        <div className="nav-sidebar__inner">
          {isLoading ? (
            <div className="nav-sidebar__loading">Loading…</div>
          ) : (
            <ProjectTree projects={projects} />
          )}
        </div>
      </aside>

      <section className="nav-main" data-tour="main-panel">
        <NavCenter
          loading={isLoading}
          projects={projects}
          selectedProjectId={selectedProjectId}
          selectedPaperId={selectedPaperId}
          archived={archived}
        />
      </section>

      <aside className="nav-details" data-tour="details-panel">
        <DetailsPanel
          projects={projects}
          selectedProjectId={selectedProjectId}
          selectedPaperId={selectedPaperId}
        />
      </aside>
    </div>
  );
}

// Mobile Navigator — three permanently-stacked sections (no drawer):
//   project tree · preview pane (NavCenter) · details panel.
function NavigatorMobile({
  projects,
  isLoading,
  selectedProjectId,
  selectedPaperId,
  archived,
}: {
  projects: Project[];
  isLoading: boolean;
  selectedProjectId: string | null;
  selectedPaperId: string | null;
  archived: boolean;
}) {
  return (
    <div className="nav-shell nav-shell--mobile">
      <aside className="nav-sidebar nav-mobile__tree" data-tour="sidebar">
        <div className="nav-sidebar__inner">
          {isLoading ? (
            <div className="nav-sidebar__loading">Loading…</div>
          ) : (
            <ProjectTree projects={projects} />
          )}
        </div>
      </aside>

      <div className="nav-mobile__content">
        <section className="nav-main" data-tour="main-panel">
          <NavCenter
            loading={isLoading}
            projects={projects}
            selectedProjectId={selectedProjectId}
            selectedPaperId={selectedPaperId}
            archived={archived}
          />
        </section>
        <aside className="nav-details" data-tour="details-panel">
          <DetailsPanel
            projects={projects}
            selectedProjectId={selectedProjectId}
            selectedPaperId={selectedPaperId}
          />
        </aside>
      </div>
    </div>
  );
}

const STATUS_KEY: Record<PaperStatus, string> = {
  queued: "paper_status.queued",
  staged: "paper_status.staged",
  processing: "paper_status.processing",
  ready: "paper_status.ready",
  in_review: "paper_status.in_review",
  completed: "paper_status.completed",
  failed: "paper_status.failed",
  quota_exceeded: "paper_status.quota_exceeded",
};

function statusDotClass(status: PaperStatus): string {
  switch (status) {
    case "completed":
      return "status-dot--completed";
    case "staged":
    case "processing":
      return "status-dot--processing";
    case "ready":
    case "in_review":
      return "status-dot--in-review";
    case "failed":
      return "status-dot--failed";
    case "quota_exceeded":
      return "status-dot--quota-exceeded";
    default:
      return "status-dot--queued";
  }
}

// Line variant pattern from mockup JS:
//   i%5===0 → 'written-dark'; otherwise i%3!==2 → 'written'; else '' (empty).
const PREVIEW_LINES: ReadonlyArray<"" | "written" | "written-dark"> = Array.from(
  { length: 28 },
  (_, i) => {
    if (i % 5 === 0) return "written-dark";
    if (i % 3 !== 2) return "written";
    return "";
  },
);

function NavCenter({
  loading,
  projects,
  selectedProjectId,
  selectedPaperId,
  archived,
}: {
  loading: boolean;
  projects: Project[];
  selectedProjectId: string | null;
  selectedPaperId: string | null;
  archived: boolean;
}) {
  const { t } = useTranslation();
  const [, setParams] = useSearchParams();
  const { mutate } = useSWRConfig();
  const [uploadOpen, setUploadOpen] = useState(false);

  // Preview zoom — lifted here so the reset button can live in the panel header.
  const [previewZoom, setPreviewZoom] = useState(1.0);
  const resetModelUi = useModelUiStore((s) => s.reset);
  const modelAssignMode = useModelUiStore((s) => s.assignMode);
  const setExamView = useExamUiStore((s) => s.setExamView);
  useEffect(() => {
    setPreviewZoom(1.0);
    resetModelUi();
    setExamView("original");
  }, [selectedPaperId, selectedProjectId, resetModelUi, setExamView]);

  // Exam highlight: the active task marks its located section in the Exam PDF.
  const examProjectId =
    selectedPaperId === EXAM_PAPER_ID && selectedProjectId ? Number(selectedProjectId) : null;
  const { data: examTasksData } = useExamTasks(examProjectId);
  const activeTaskId = useExamUiStore((s) => s.activeTaskId);
  const examView = useExamUiStore((s) => s.examView);
  const activeExamTask = examTasksData?.tasks.find((tk) => tk.id === activeTaskId);
  const examHighlight =
    activeExamTask?.bbox && activeExamTask.bbox_page != null
      ? {
          page: activeExamTask.bbox_page + 1,
          bbox: activeExamTask.bbox,
          frame: activeExamTask.block_bbox ?? activeExamTask.bbox,
        }
      : undefined;

  const projectsKey = `/api/projects/?archived=${archived ? 1 : 0}`;

  if (loading) return null;

  const project = selectedProjectId
    ? projects.find((p) => String(p.id) === selectedProjectId)
    : undefined;

  const paper =
    project && selectedPaperId && selectedPaperId !== MODEL_PAPER_ID && selectedPaperId !== EXAM_PAPER_ID
      ? project.papers.find((p) => String(p.id) === selectedPaperId)
      : undefined;

  const isModelSelected = selectedPaperId === MODEL_PAPER_ID;
  const isExamSelected = selectedPaperId === EXAM_PAPER_ID;

  // Default — nothing selected
  if (!project) {
    return (
      <div className="nav-main__default">
        <div className="nav-main__logo-row">
          <img src="/logo.svg" alt="" className="nav-main__logo" />
          <div className="nav-main__tagline">{t("navigator.tagline")}</div>
        </div>
      </div>
    );
  }

  const zoomed = previewZoom !== 1.0;

  // Exam selected
  if (isExamSelected) {
    const hasDigitized = !!project.exam_digitized_pdf_path;
    const showDigitized = hasDigitized && examView === "digitized";
    const examPdfUrl = project.exam_pdf_path
      ? showDigitized
        ? `/api/projects/${project.id}/exam_digitized_pdf`
        : `/api/projects/${project.id}/exam_pdf`
      : undefined;
    return (
      <div className={`nav-main__content${zoomed ? " nav-main__content--zoomed" : ""}`}>
        <div className="nav-main__preview-header">
          <span className="nav-main__section-title">
            {showDigitized ? t("navigator.exam_preview_digitized") : t("navigator.exam_preview")}
          </span>
          <div className="nav-main__preview-actions">
            {zoomed && (
              <button type="button" className="nav-main__zoom-reset" onClick={() => setPreviewZoom(1.0)} title="Reset zoom">↔</button>
            )}
            {hasDigitized && (
              <button
                type="button"
                className={"nav-main__doc-toggle" + (showDigitized ? " nav-main__doc-toggle--active" : "")}
                onClick={() => setExamView(showDigitized ? "original" : "digitized")}
                title={t(showDigitized ? "exam_panel.view_original" : "exam_panel.view_digitized")}
                aria-label={t(showDigitized ? "exam_panel.view_original" : "exam_panel.view_digitized")}
                aria-pressed={showDigitized}
              >
                <IconPage />
              </button>
            )}
          </div>
        </div>
        <PreviewPage label={t("exam_panel.title")} pdfUrl={examPdfUrl} zoom={previewZoom} onZoomChange={setPreviewZoom} highlight={examHighlight} multiPage scrollKey={activeTaskId} />
      </div>
    );
  }

  // Model solution selected — Assign mode shows the full solution to highlight;
  // otherwise the editable per-task paper stack.
  if (isModelSelected) {
    if (modelAssignMode && project.model_full_pdf_path) {
      return <ModelAssignPreview project={project} />;
    }
    return (
      <div className="nav-main__content">
        <ModelSolutionStack project={project} />
      </div>
    );
  }

  // Paper selected
  if (paper) {
    return (
      <div className={`nav-main__content${zoomed ? " nav-main__content--zoomed" : ""}`}>
        <div className="nav-main__preview-header">
          <span className="nav-main__section-title">{paper.name} — Preview</span>
          {zoomed && (
            <button type="button" className="nav-main__zoom-reset" onClick={() => setPreviewZoom(1.0)} title="Reset zoom">↔</button>
          )}
        </div>
        <PreviewPage label={paper.name} pdfUrl={`/api/papers/${paper.id}/pdf`} zoom={previewZoom} onZoomChange={setPreviewZoom} multiPage />
      </div>
    );
  }

  // Project selected — upload zone + paper list
  const visiblePapers = project.papers.filter((p) => !p.archived);

  function selectPaper(paperId: number) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("paper", String(paperId));
      return next;
    });
    const { active, papers } = useTourStore.getState();
    if (active && papers && paperId === papers.inReview) {
      // Delay so React can commit PaperPanel to the DOM before driver.js
      // queries [data-tour="go-to-review-btn"]. Without the delay the element
      // doesn't exist yet and the spotlight lands in the wrong place.
      setTimeout(() => advanceTour(), 250);
    }
  }

  return (
    <>
      <div className="nav-main__content">
        <div className="nav-main__section-title">Add Papers</div>
        <div
          className="nav-main__upload-zone"
          data-tour="upload-zone"
          onClick={() => setUploadOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setUploadOpen(true)}
        >
          <div className="nav-main__upload-zone-icon">⬆</div>
          <div className="nav-main__upload-zone-label">Drop PDFs or images here</div>
          <div className="nav-main__upload-zone-sub">
            PDF · JPEG · PNG · TIFF · multi-file supported
          </div>
          <button
            type="button"
            className="nav-main__upload-zone-btn"
            onClick={(e) => {
              e.stopPropagation();
              setUploadOpen(true);
            }}
          >
            Browse files
          </button>
        </div>

        {visiblePapers.length > 0 && (
          <>
            <div className="nav-main__section-title">Papers in this project</div>
            <div className="nav-main__paper-grid" data-tour="paper-grid">
              {visiblePapers.map((p) => (
                <PaperThumbRow key={p.id} paper={p} onSelect={selectPaper} />
              ))}
            </div>
          </>
        )}
      </div>

      {uploadOpen && (
        <UploadPapersDialog
          projectId={project.id}
          onClose={() => setUploadOpen(false)}
          onComplete={() => void mutate(projectsKey)}
        />
      )}
    </>
  );
}

/** Streams a PDF by URL (byte-range) and renders it at the requested pixel width. */
interface PreviewHighlight {
  page: number; // 1-based
  bbox: [number, number, number, number]; // evidence anchor — fill (x0,y0,x1,y1 norm 0..1)
  frame: [number, number, number, number]; // whole task-description block — border
}

/** Folded-corner page glyph — matches the Paper review transcript toggle. */
function IconPage() {
  return (
    <svg width="11" height="13" viewBox="0 0 11 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}>
      <path d="M2 1.5h5L9.5 5v6.5a.5.5 0 0 1-.5.5H2a.5.5 0 0 1-.5-.5V2a.5.5 0 0 1 .5-.5z" />
      <path d="M7 1.5V5h2.5" />
    </svg>
  );
}

/** The active task's highlight (frame + evidence fill), positioned over its page. */
function HighlightOverlay({ highlight }: { highlight: PreviewHighlight }) {
  const [ex0, ey0, ex1, ey1] = highlight.bbox;
  const [fx0, fy0, fx1, fy1] = highlight.frame;
  const pct = (a: number, b: number) => `${(b - a) * 100}%`;
  return (
    <>
      {/* Frame around the whole task description. */}
      <div
        className="nav-main__exam-frame"
        style={{ left: `${fx0 * 100}%`, top: `${fy0 * 100}%`, width: pct(fx0, fx1), height: pct(fy0, fy1) }}
        aria-hidden
      />
      {/* Fill over the located evidence snippet. */}
      <div
        className="nav-main__exam-highlight"
        style={{ left: `${ex0 * 100}%`, top: `${ey0 * 100}%`, width: pct(ex0, ex1), height: pct(ey0, ey1) }}
        aria-hidden
      />
    </>
  );
}

// A skeleton sized to one page, so the preview reserves space and shows a clean
// loading state instead of react-pdf's "No PDF file specified" while it streams.
function PageSkeleton({ width }: { width: number }) {
  return <div className="nav-main__pdf-skeleton" style={{ width, aspectRatio: "0.707" }} />;
}

// One page that only renders (paints to canvas) once it's near the viewport, so
// a long exam shows page 1 immediately instead of rendering every page up front.
function LazyPage({
  pageNumber,
  width,
  highlight,
  active,
  activeRef,
}: {
  pageNumber: number;
  width: number;
  highlight?: PreviewHighlight;
  active: boolean;
  activeRef: (node: HTMLDivElement | null) => void;
}) {
  const localRef = useRef<HTMLDivElement | null>(null);
  const [render, setRender] = useState(pageNumber === 1); // first page eagerly

  useEffect(() => {
    if (render) return;
    const el = localRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setRender(true);
          io.disconnect();
        }
      },
      { rootMargin: "500px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [render]);

  const setRefs = (node: HTMLDivElement | null) => {
    localRef.current = node;
    if (active) activeRef(node);
  };

  return (
    <div ref={setRefs} className="nav-main__pdf-page" style={render ? undefined : { width, aspectRatio: "0.707" }}>
      {render && (
        <Page
          pageNumber={pageNumber}
          width={width}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          loading={<PageSkeleton width={width} />}
        />
      )}
      {render && active && highlight && <HighlightOverlay highlight={highlight} />}
    </div>
  );
}

function PdfPageThumb({
  url,
  width,
  pageNumber = 1,
  highlight,
  multiPage = false,
  scrollKey,
}: {
  url: string;
  width: number;
  pageNumber?: number;
  highlight?: PreviewHighlight;
  // multiPage renders every page stacked (scrollable) — used for the Exam
  // preview, which can run to several pages; the single-page mode (default) is
  // the paper "Page 1 Preview".
  multiPage?: boolean;
  // Changes when the active task changes, so the highlighted page scrolls into view.
  scrollKey?: number | null;
}) {
  const [numPages, setNumPages] = useState(0);
  const activePageRef = useRef<HTMLDivElement | null>(null);

  // Hand react-pdf the URL (not a pre-fetched ArrayBuffer) so pdf.js streams it
  // with byte-range requests — page 1 paints before the whole file downloads.
  // Memoised so the file identity is stable across renders (else it reloads).
  const file = useMemo(() => ({ url, withCredentials: true }), [url]);
  const skeleton = <PageSkeleton width={width} />;

  // Bring the active task's page into view when the selection changes.
  useEffect(() => {
    if (multiPage && highlight && activePageRef.current) {
      activePageRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [multiPage, highlight?.page, scrollKey]);

  if (multiPage) {
    return (
      <Document
        file={file}
        loading={skeleton}
        noData={skeleton}
        error={null}
        onLoadSuccess={(pdf: { numPages: number }) => setNumPages(pdf.numPages)}
      >
        {Array.from({ length: numPages }, (_, i) => {
          const p = i + 1;
          return (
            <LazyPage
              key={p}
              pageNumber={p}
              width={width}
              highlight={highlight}
              active={highlight?.page === p}
              activeRef={(node) => { activePageRef.current = node; }}
            />
          );
        })}
      </Document>
    );
  }

  const showHighlight = highlight && highlight.page === pageNumber;
  return (
    <div style={{ position: "relative" }}>
      <Document file={file} loading={skeleton} noData={skeleton} error={null}>
        <Page
          pageNumber={pageNumber}
          width={width}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          loading={skeleton}
        />
      </Document>
      {showHighlight && highlight && <HighlightOverlay highlight={highlight} />}
    </div>
  );
}

function PaperThumbRow({
  paper,
  onSelect,
}: {
  paper: Paper;
  onSelect: (id: number) => void;
}) {
  const { t } = useTranslation();
  const tourPapers = useTourStore((s) => s.papers);
  const isTourTarget = tourPapers?.inReview === paper.id;
  return (
    <div
      className="nav-main__paper-row"
      onClick={() => onSelect(paper.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onSelect(paper.id)}
      {...(isTourTarget ? { "data-tour": "paper-row-in-review" } : {})}
    >
      <div className="nav-main__paper-thumb">
        <PdfPageThumb url={`/api/papers/${paper.id}/pdf`} width={32} />
      </div>
      <div className="nav-main__paper-info">
        <div className="nav-main__paper-name">{paper.name}</div>
        <div className="nav-main__paper-meta">
          <span
            className={`status-dot ${statusDotClass(paper.status)}`}
            style={{ display: "inline-block", marginRight: 5, verticalAlign: "middle" }}
          />
          {t(STATUS_KEY[paper.status])}
          {paper.page_count ? ` · ${paper.page_count} pp` : ""}
        </div>
      </div>
    </div>
  );
}

function PreviewPage({
  label,
  pdfUrl,
  zoom,
  onZoomChange,
  highlight,
  multiPage = false,
  scrollKey,
}: {
  label: string;
  pdfUrl?: string;
  zoom: number;
  onZoomChange: (z: number) => void;
  highlight?: PreviewHighlight;
  multiPage?: boolean;
  scrollKey?: number | null;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [thumbWidth, setThumbWidth] = useState(0);
  const zoomRef = useRef(zoom);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  const onZoomChangeRef = useRef(onZoomChange);
  useEffect(() => { onZoomChangeRef.current = onZoomChange; }, [onZoomChange]);

  const zoomed = zoom !== 1.0;

  // Re-observe the new element whenever the layout switches between default/zoomed.
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setThumbWidth(Math.round(entry.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [zoomed]);

  // Re-attach wheel handler on the new element when layout switches.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      onZoomChangeRef.current(Math.max(0.4, Math.min(4.0, zoomRef.current + (e.deltaY > 0 ? -0.12 : 0.12))));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomed]);

  if (pdfUrl != null) {
    // Zoomed: fill the entire panel, render PDF at actual zoomed width so
    // overflow-auto scrolling works correctly.
    if (zoomed) {
      const pdfW = thumbWidth > 0 ? Math.round(thumbWidth * zoom) : 0;
      return (
        <div className="nav-main__preview-fill" ref={wrapRef}>
          {pdfW > 0 && (
            <div style={{ width: pdfW, margin: "0 auto" }}>
              <PdfPageThumb key={pdfUrl} url={pdfUrl} width={pdfW} pageNumber={highlight?.page ?? 1} highlight={highlight} multiPage={multiPage} scrollKey={scrollKey} />
            </div>
          )}
        </div>
      );
    }
    // Default: 75 % centred preview.
    return (
      <div className="nav-main__preview-wrap nav-main__preview-wrap--pdf" ref={wrapRef}>
        {thumbWidth > 0 &&
          (multiPage ? (
            <PdfPageThumb key={pdfUrl} url={pdfUrl} width={thumbWidth} highlight={highlight} multiPage scrollKey={scrollKey} />
          ) : (
            <div className="nav-main__pdf-preview">
              <PdfPageThumb key={pdfUrl} url={pdfUrl} width={thumbWidth} pageNumber={highlight?.page ?? 1} highlight={highlight} />
            </div>
          ))}
      </div>
    );
  }

  // Non-PDF skeleton placeholder — use CSS transform (no scroll needed).
  return (
    <div className={zoomed ? "nav-main__preview-fill" : "nav-main__preview-wrap"} ref={wrapRef}
      style={zoomed ? { overflow: "hidden", alignItems: "flex-start", justifyContent: "center" } : undefined}
    >
      <div
        className="nav-main__preview-page"
        style={zoomed ? { transform: `scale(${zoom})`, transformOrigin: "top center" } : undefined}
      >
        <div className="nav-main__preview-label">{label}</div>
        {PREVIEW_LINES.map((variant, i) => (
          <div
            key={i}
            className={`nav-main__preview-line${variant ? ` nav-main__preview-line--${variant}` : ""}`}
          />
        ))}
      </div>
    </div>
  );
}
